import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../firebase/AuthProvider";
import { Conversation, getConversationById, getRecentChatActivity } from "../../../services/chatService/ChatService";
import { selectedConversation$, floatingChatEscape$, ResourceComposeRequest } from "../../../services/Subjects/ChatSubjects";
import ConversationList, { ConversationListRef } from "./ConversationListComponent";

import './chat.css';
import GlobalChatWindow from "./ChatWindow";
import { currentConversationId$ } from "../../../services/Subjects/userSubject";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Activity, MessageCircleMore, Plus, Search, Sparkles } from "lucide-react";
import { blurFocusedChatControl, dismissTopChatEscapeLayer } from "../../../utilities/chat/chatEscapeLayers";
import ChatActivityView from "./ChatActivityView";
import { useChatConnection } from "../../../contexts/ChatConnectionContext";
import ChatGlobalSearchView from "./ChatGlobalSearchView";
import { ReactionBadge } from "./PopupReactionActivity";

const ChatPaneSkeleton: React.FC<{ compact?: boolean }> = ({ compact = false }) => (
    <div className="dark-surface flex h-full min-h-0 flex-col bg-white dark:bg-slate-900" aria-label="Loading conversation" aria-busy="true">
        <div className={`flex shrink-0 items-center gap-3 px-3 ${compact ? "h-14" : "h-16"}`}>
            <div className="h-9 w-9 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />
            <div className="min-w-0 flex-1"><div className="h-3.5 w-28 animate-pulse rounded bg-slate-200 dark:bg-slate-800" /><div className="mt-2 h-2.5 w-20 animate-pulse rounded bg-slate-100 dark:bg-slate-800/60" /></div>
            <div className="h-8 w-8 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800" />
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-hidden px-4 py-5">
            {[42, 68, 54, 76, 48].map((width, index) => <div key={index} className={`flex gap-2 ${index % 2 ? "flex-row-reverse" : ""}`}><div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" /><div className="w-full max-w-[78%]"><div className={`h-2.5 animate-pulse rounded bg-slate-100 dark:bg-slate-800/60`} style={{ width: `${Math.max(28, width - 18)}%` }} /><div className="mt-2 h-9 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-800" style={{ width: `${width}%` }} /></div></div>)}
        </div>
        <div className="m-3 h-12 shrink-0 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800" />
    </div>
);

interface ChatPageProps {
    resourceComposeRequest?: ResourceComposeRequest | null;
    onResourceComposeFinished?: () => void;
    isSideView?: boolean;
    handleShowChatHead?: (show: boolean) => void;
    showHeader?: boolean;
    handleCloseChat?: () => void;
    addConversationButtonRef?: React.RefObject<HTMLButtonElement>;
    handleToggleAddConversation?: () => void;
    isVisible?: boolean;
    hasUnreadActivity?: boolean;
    linkedThreadRootId?: number;
    linkedMessageId?: number;
    onClearLinkedTarget?: () => void;
    /** Popup only: unread-reaction summary rendered above the conversation rows. */
    reactionActivitySlot?: React.ReactNode;
    reactionBadges?: Record<number, ReactionBadge>;
    /** Popup only: called when a conversation is opened from the list. */
    onConversationOpened?: (conversationId: number) => void;
}

const ChatPage = React.forwardRef<ConversationListRef, ChatPageProps>((props, ref) => {
    const { isSideView = false, handleShowChatHead, showHeader = true, handleCloseChat, isVisible = true,
        addConversationButtonRef, handleToggleAddConversation, hasUnreadActivity = false,
        linkedThreadRootId, linkedMessageId, onClearLinkedTarget, reactionActivitySlot, reactionBadges, onConversationOpened } = props;
    const { currentUser } = useAuth();
    const { connection, connectionVersion } = useChatConnection();
    const { conversationId } = useParams<{ conversationId?: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const requestedSection = searchParams.get("view");
    const activeSection: "messages" | "activity" | "search" = !isSideView && requestedSection === "activity"
        ? "activity"
        : !isSideView && requestedSection === "search"
            ? "search"
            : "messages";
    const initialThreadRootId = isSideView ? linkedThreadRootId : Number(searchParams.get("thread")) || undefined;
    const focusedMessageId = isSideView ? linkedMessageId : Number(searchParams.get("message")) || undefined;

    const clearLinkedTarget = useCallback(() => {
        if (isSideView) {
            onClearLinkedTarget?.();
            return;
        }
        if (conversationId) navigate(`/chat/${conversationId}`, { replace: true });
    }, [conversationId, isSideView, navigate, onClearLinkedTarget]);

    const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
    const [tokenState, setTokenState] = useState({ userId: '', token: '' });
    const authToken = tokenState.userId === currentUser?.uid ? tokenState.token : '';
    const [isChatOpen, setIsChatOpen] = useState<boolean>(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [hasFullPageUnreadActivity, setHasFullPageUnreadActivity] = useState(false);
    const chatWindowSurfaceRef = useRef<HTMLDivElement>(null);
    const effectiveUnreadActivity = isSideView ? hasUnreadActivity : hasFullPageUnreadActivity;

    useEffect(() => {
        if (!props.resourceComposeRequest) return;
        setIsChatOpen(false); currentConversationId$.next(0); handleShowChatHead?.(true);
    }, [props.resourceComposeRequest, handleShowChatHead]);

    const composeRequestRef = useRef(props.resourceComposeRequest);
    composeRequestRef.current = props.resourceComposeRequest;
    const handleChatOpen = useCallback(() => setIsChatOpen(true), []);

    const handleSelectConversation = useCallback((conversation: Conversation, doNavigate = true) => {
        currentConversationId$.next(conversation.id);
        setSelectedConversation(conversation);
        if (doNavigate && !isSideView) navigate(`/chat/${conversation.id}`);
        handleChatOpen();
        if (handleShowChatHead) handleShowChatHead(false);
    }, [navigate, handleChatOpen, handleShowChatHead, isSideView]);

    const handleChatClose = useCallback(() => {
        currentConversationId$.next(0);
        selectedConversation$.next(0);

        setIsChatOpen(false);

        if (handleShowChatHead) handleShowChatHead(true);
        if (!isSideView) navigate('/chat');
    }, [navigate, handleShowChatHead, isSideView]);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 767px)');
        const handleResize = () => setIsMobile(mediaQuery.matches);
        mediaQuery.addEventListener('change', handleResize);
        return () => mediaQuery.removeEventListener('change', handleResize);
    }, []);

    useEffect(() => {
        let cancelled = false;
        if (!currentUser) { setTokenState({ userId: '', token: '' }); return; }
        const userId = currentUser.uid;
        currentUser.getIdToken().then(token => {
            if (!cancelled) setTokenState({ userId, token });
        }).catch(() => { if (!cancelled) setTokenState({ userId, token: '' }); });
        return () => { cancelled = true; };
    }, [currentUser]);

    const refreshActivityBadge = useCallback(async () => {
        if (!currentUser || isSideView) return;
        try {
            const activities = await getRecentChatActivity(await currentUser.getIdToken(), 1, 30);
            setHasFullPageUnreadActivity(activities.some(activity => !activity.read));
        } catch (error) {
            console.error("Unable to refresh Chat activity badge", error);
        }
    }, [currentUser, isSideView]);

    useEffect(() => { void refreshActivityBadge(); }, [connectionVersion, refreshActivityBadge]);

    useEffect(() => {
        if (!connection || isSideView) return;
        const receiveActivity = () => setHasFullPageUnreadActivity(true);
        const activityRead = () => void refreshActivityBadge();
        connection.on("ReceiveChatActivity", receiveActivity);
        window.addEventListener("chat-activity-read", activityRead);
        return () => {
            connection.off("ReceiveChatActivity", receiveActivity);
            window.removeEventListener("chat-activity-read", activityRead);
        };
    }, [connection, isSideView, refreshActivityBadge]);

    useEffect(() => {
        if (!isSideView && conversationId && authToken && !props.resourceComposeRequest) {
            if (selectedConversation?.id.toString() === conversationId) return;
            getConversationById(authToken, parseInt(conversationId)).then(conversation => {
                if (conversation && !composeRequestRef.current) handleSelectConversation(conversation, false);
            });
        }
    }, [conversationId, authToken, selectedConversation?.id, handleSelectConversation, props.resourceComposeRequest, isSideView]);

    useEffect(() => {
        if (!isSideView && conversationId && activeSection === "messages" && !props.resourceComposeRequest) setIsChatOpen(true);
    }, [activeSection, conversationId, props.resourceComposeRequest, isSideView]);

    useEffect(() => {
        if (activeSection === "activity") return;
        const subscription = selectedConversation$.subscribe(selectedConversationId => {
            if (composeRequestRef.current) return;
            if (selectedConversationId) {
                setIsChatOpen(true);
                handleShowChatHead?.(false);
            }
            if (selectedConversationId && authToken) {
                getConversationById(authToken, selectedConversationId).then(conversation => {
                    if (conversation && !composeRequestRef.current) {
                        // A BehaviorSubject immediately replays its current value.
                        // When a message/activity deep link already targets that
                        // conversation, navigating again would strip its query params.
                        handleSelectConversation(conversation, conversation.id.toString() !== conversationId);
                    }
                });
            }
        });
        return () => subscription.unsubscribe();
    }, [activeSection, authToken, conversationId, handleSelectConversation, handleShowChatHead]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!isVisible || event.defaultPrevented || dismissTopChatEscapeLayer(event)) return;
            if (event.key === 'Escape' && document.querySelector('.tippy-box[data-state="visible"]')) return;
            if (blurFocusedChatControl(event)) return;
            if (event.key === 'Escape') {
                // Check if a Thread is open via DOM
                const isThreadOpen = !!document.querySelector('.thread-panel-container');

                // IF a thread is open, do NOTHING here. 
                // We will let GlobalChatWindow handle the thread closure.
                if (isThreadOpen) return;

                if (isChatOpen) {
                    handleChatClose();
                } else if (isSideView && handleCloseChat) {
                    handleCloseChat();
                }
            }
        };

        const modalEscape = isSideView && isVisible ? floatingChatEscape$.subscribe(handleKeyDown) : undefined;
        window.addEventListener('keydown', handleKeyDown);
        return () => { modalEscape?.unsubscribe(); window.removeEventListener('keydown', handleKeyDown); };
    }, [isChatOpen, isSideView, handleChatClose, handleCloseChat, isVisible]);


    const userId = currentUser?.uid;
    const singlePane = isMobile || isSideView;

    useEffect(() => {
        const surface = chatWindowSurfaceRef.current;
        if (!surface) return;

        // Preserve the selected chat while it is behind the conversation list,
        // but keep its controls out of the keyboard/accessibility focus order.
        if (singlePane && !isChatOpen) surface.setAttribute("inert", "");
        else surface.removeAttribute("inert");

        return () => surface.removeAttribute("inert");
    }, [isChatOpen, singlePane]);

    const removeActiveMeetingStatusFromSelectedConversation = () => {
        if (selectedConversation) {
            setSelectedConversation({ ...selectedConversation, activeMeeting: undefined });
        }
    }

    return (
        <div data-chat-primary-region tabIndex={-1} className={`relative flex min-h-0 flex-1 flex-col overflow-hidden outline-none ${isSideView ? 'h-full' : ''}`}>
            {!isSideView && (
                <nav className="dark-surface flex shrink-0 items-center gap-1 bg-white px-4 py-2 dark:bg-slate-900" aria-label="Chat sections">
                    <button
                        type="button"
                        onClick={() => navigate(conversationId ? `/chat/${conversationId}` : "/chat")}
                        className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition ${activeSection === "messages" ? "bg-purple-50 text-[#6D4DB3] dark:bg-purple-400/10 dark:text-purple-300" : "text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800"}`}
                    >
                        <MessageCircleMore size={17} /> Messages
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate("/chat?view=activity")}
                        className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition ${activeSection === "activity" ? "bg-purple-50 text-[#6D4DB3] dark:bg-purple-400/10 dark:text-purple-300" : "text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800"}`}
                    >
                        <span className="relative">
                            <Activity size={17} />
                            {effectiveUnreadActivity && <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-[#7E57C2] ring-2 ring-white dark:ring-slate-900" />}
                        </span>
                        Activity
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate("/chat?view=search")}
                        className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition ${activeSection === "search" ? "bg-purple-50 text-[#6D4DB3] dark:bg-purple-400/10 dark:text-purple-300" : "text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800"}`}
                    >
                        <Search size={17} /> Search
                    </button>
                </nav>
            )}

            {activeSection === "activity" && <ChatActivityView />}
            {activeSection === "search" && <ChatGlobalSearchView />}
            <div
                aria-hidden={activeSection !== "messages"}
                className={`chat_wrapper flex-1 relative min-h-0 overflow-hidden ${activeSection === "messages" ? "flex" : "hidden"} ${isSideView ? 'h-full flex-1 flex-col' : ''}`}
                style={{ height: '100%' }}
            >
            {/* Left Pane (Conversation List) */}
            {authToken && userId && (
                <div
                    className={`flex h-full min-h-0 overflow-hidden transition-opacity duration-300 ${isSideView ? 'flex-1 w-full' : 'chat_wrapper_left'} ${singlePane && isChatOpen ? "opacity-0 pointer-events-none" : "opacity-100"
                        }`}
                >
                    <ConversationList
                        ref={ref}
                        resourceComposeRequest={props.resourceComposeRequest}
                        onResourceComposeFinished={props.onResourceComposeFinished}
                        onResourceComposeBlocked={() => {
                            if (selectedConversation) { currentConversationId$.next(selectedConversation.id); setIsChatOpen(true); handleShowChatHead?.(false); }
                        }}
                        authToken={authToken}
                        userId={userId}
                        selectedConversationId={singlePane && !isChatOpen ? null : selectedConversation?.id || null}
                        onSelectConversation={conversation => {
                            const sharing = !!props.resourceComposeRequest;
                            onConversationOpened?.(conversation.id);
                            handleSelectConversation(conversation);
                            if (sharing) window.requestAnimationFrame(() => {
                                const surface = chatWindowSurfaceRef.current;
                                if (surface?.isConnected && surface.getAttribute('aria-hidden') !== 'true') {
                                    surface.querySelector<HTMLElement>('[role="textbox"][contenteditable="true"]')?.focus({ preventScroll: true });
                                }
                            });
                        }}
                        showHeader={showHeader}
                        removeActiveMeetingFromSelectedConversation={removeActiveMeetingStatusFromSelectedConversation}
                        onCloseSideChat={handleCloseChat}
                        handleConversation={handleToggleAddConversation}
                        conversationRef={addConversationButtonRef}
                        isSideView={isSideView}
                        listTopSlot={isSideView ? reactionActivitySlot : undefined}
                        reactionBadges={isSideView ? reactionBadges : undefined}
                    />
                </div>
            )}
            {(!authToken || !userId) && (
                <div className={`h-full min-h-0 ${isSideView ? "flex-1 w-full" : "chat_wrapper_left"}`}>
                    <ChatPaneSkeleton compact />
                </div>
            )}

            {/* Right Pane (Chat Window) with Slide Logic */}
            <div
                ref={chatWindowSurfaceRef}
                aria-hidden={singlePane && !isChatOpen}
                className={`dark-surface min-h-0 h-full bg-white dark:bg-slate-900 transition-transform duration-300 ease-in-out transform ${
                    singlePane
                        ? `fixed inset-0 z-10 ${isChatOpen ? "translate-x-0" : "translate-x-full pointer-events-none"} ${isSideView ? "w-full" : ""}`
                        : "chat_wrapper_right relative translate-x-0"
                }`}
            >
                {selectedConversation && currentUser ? (
                    <GlobalChatWindow
                        key={selectedConversation.id}
                        conversation={selectedConversation}
                        handleChatClose={handleChatClose}
                        singlePane={singlePane}
                        isShowCallingIcon={!selectedConversation.channel}
                        isSideView={isSideView}
                        isActiveView={isVisible && activeSection === "messages" && (!singlePane || isChatOpen)}
                        initialThreadRootId={initialThreadRootId}
                        focusedMessageId={focusedMessageId}
                        onClearLinkedTarget={clearLinkedTarget}
                    />
                ) : isChatOpen ? (
                    <ChatPaneSkeleton compact={singlePane} />
                ) : !isSideView ? (
                    <div className="chat-empty-selection start_chat_read flex h-full min-h-0 items-center justify-center bg-white px-6 py-10">
                        <div className="flex w-full max-w-md flex-col items-center text-center">
                            <div className="relative mb-6">
                                <div className="absolute inset-0 scale-150 rounded-full bg-[#7E57C2]/5 blur-2xl" />
                                <div className="chat-empty-icon relative flex h-20 w-20 items-center justify-center rounded-[22px] border border-[#7E57C2]/15 bg-[#7E57C2]/10 text-[#7E57C2] shadow-sm">
                                    <MessageCircleMore size={36} strokeWidth={1.8} />
                                </div>
                                <div className="chat-empty-sparkle absolute -right-2 -top-2 flex h-8 w-8 items-center justify-center rounded-xl border border-purple-100 bg-white text-[#7E57C2] shadow-sm">
                                    <Sparkles size={15} strokeWidth={2} />
                                </div>
                            </div>

                            <h2 className="chat-empty-title m-0 text-xl font-semibold tracking-tight text-slate-900">
                                Select a conversation
                            </h2>
                            <p className="chat-empty-copy mb-0 mt-2 max-w-sm text-sm leading-6 text-slate-500">
                                Choose a conversation from the list to view messages, share files, or continue where you left off.
                            </p>

                            {handleToggleAddConversation && (
                                <button
                                    type="button"
                                    onClick={handleToggleAddConversation}
                                    className="mt-6 inline-flex items-center gap-2 rounded-xl border border-[#7E57C2]/20 bg-[#7E57C2] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#6F49B5] hover:shadow-md focus:outline-none focus:ring-4 focus:ring-[#7E57C2]/15"
                                >
                                    <Plus size={17} strokeWidth={2.4} />
                                    New message
                                </button>
                            )}

                            <div className="chat-empty-hint mt-8 flex items-center gap-2 text-xs text-slate-400">
                                <span className="chat-empty-divider h-px w-8 bg-slate-200" />
                                <span>Your conversations stay available on the left</span>
                                <span className="chat-empty-divider h-px w-8 bg-slate-200" />
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
            </div>
        </div>
    );
});

export default ChatPage;
